import { useMemo, useState } from 'react';

import { SparkInput } from '../components/SparkInput';

interface Command {
  entry: string;
  example?: string;
  description: string;
}

interface Group {
  title: string;
  summary: string;
  commands: Command[];
}

/**
 * Every host entry the cryptic workspace answers today. Keep this list in step
 * with backend/internal/cryptic/parse.go when an entry is added.
 */
const GROUPS: Group[] = [
  {
    title: 'Availability',
    summary: 'Search flights between two cities on a date.',
    commands: [
      { entry: '1<DATE><FROM><TO>', example: '110NOVJFKLAX', description: 'Flight availability for a city pair on a date' },
      { entry: '1<DATE><FROM><TO><TIME>', example: '110NOVJFKLAX830A', description: 'Availability from a departure time (8A, 830A, 12N, 1830)' },
      { entry: '1<DATE><FROM><TO>‡<AIRLINE>', example: '110NOVJFKLAX‡AA/DL', description: 'Availability for up to three carriers (‡, ¥ or +)' },
      { entry: '1*', description: 'Redisplay the last availability' },
    ],
  },
  {
    title: 'Sell',
    summary: 'Book seats from an availability display.',
    commands: [
      { entry: '0<SEATS><CLASS><LINE>', example: '01Y1', description: 'Sell seats in a class from an availability line' },
      { entry: '0<SEATS><CLASS><LINE><CLASS><LINE>', example: '01K1R2', description: 'Married sell across several lines' },
      { entry: '0<SEATS><CLASS><LINE>*', example: '01Y1*', description: 'Sell a line and its connection in one class' },
      { entry: '0<SEATS><CLASS><CLASS><LINE>', example: '01JC2', description: 'Sell a connection with a class per leg' },
      { entry: 'XI / X<SEG>', example: 'X1', description: 'Cancel the whole itinerary, or one segment' },
    ],
  },
  {
    title: 'Names',
    summary: 'Add passengers, children and infants.',
    commands: [
      { entry: '-<LAST>/<FIRST> <TITLE>', example: '-KHAN/RABBI MR', description: 'Add an adult passenger' },
      { entry: '-<LAST>/<FIRST>*C<AGE>', example: '-KHAN/HASAN MSTR*C08', description: 'Add a child with age' },
      { entry: '-I/<LAST>/<FIRST>*I<MONTHS>', example: '-I/KHAN/JANNAT MISS*I08', description: 'Add an infant with age in months' },
      { entry: '3CHLD/<DOB>-<NAME NO>', example: '3CHLD/12JAN18-2.1', description: 'Child date of birth SSR' },
      { entry: '3INFT/<LAST>/<FIRST>/<DOB>-<ADULT NO>', example: '3INFT/KHAN/JANNAT/12FEB26-1.1', description: 'Infant SSR on the accompanying adult' },
    ],
  },
  {
    title: 'Contacts and documents',
    summary: 'Phones, emails and passports.',
    commands: [
      { entry: '9<AGENCY> <PHONE> REF <NAME>', example: '9 SABRE BD 01755585650 REF RABBI', description: 'Agency phone field' },
      { entry: '3CTCM/<MOBILE>-<NAME NO>', example: '3CTCM/01755585650-1.1', description: 'Passenger mobile contact' },
      { entry: '3CTCE/<EMAIL>-<NAME NO>', example: '3CTCE/RABBIDU//GMAIL.COM-1.1', description: 'Passenger email contact (// for @)' },
      { entry: '3DOCS/P/…-<NAME NO>', example: '3DOCS/P/BGD/A1234567/BGD/01JAN90/M/01JAN30/KHAN/RABBI-1.1', description: 'Passport details (APIS)' },
      { entry: '3DOCS/DB/<DOB>/<G>/<LAST>/<FIRST>-<NAME NO>', example: '3DOCS/DB/09SEP92/M/RABBI/MD SHAIFULIAR-1.1', description: 'Date of birth and gender, without a passport' },
      { entry: '3DOCO/…-<NAME NO>', example: '3DOCO/BD/V/1209876/BD/12JUN22/US//12JUN24-1.1', description: 'Visa details' },
      { entry: '3PCTC/<NAME>/<PHONE>-<NAME NO>', example: '3PCTC/RAHIM KHAN/US 12125550100-1.1', description: 'Emergency contact' },
      { entry: '*P3D / *P4D', description: 'Display passport and security details' },
      { entry: '3<ITEM>.XX', example: '311.XX', description: 'Delete general facts by item number (DOCS, DOCO, SSRs) — then 6R, ER' },
    ],
  },
  {
    title: 'Special services and seats',
    summary: 'Meals, wheelchairs, VIP flags, OSI, seats and remarks.',
    commands: [
      { entry: '3<MEAL>-<NAME NO>', example: '3MOML-1.1', description: 'Meal on every flight — MOML, VGML, KSML, CHML, BBML, AVML, HNML …' },
      { entry: '3BSCT-<ADULT NO>', example: '3BSCT-1.1', description: 'Bassinet, on the adult with the infant (BBML too)' },
      { entry: '3WCHR/<TEXT>-<NAME NO>', example: '3WCHR/UNBL TO WALK LONG DSTNC NON MEDA-1.1', description: 'Wheelchair (WCHS, WCHC too)' },
      { entry: '3NSST/<TEXT>-<NAME NO>', example: '3NSST/NEED WINDOW SEAT-1.1', description: 'Non-smoking seat request' },
      { entry: '3VIP / 3VVIP / 3CVIP/<TEXT>-<NAME NO>', example: '3VIP/PAX MINISTER-1.1', description: 'VIP, VVIP and CIP flags for BG' },
      { entry: '3OSI <AIRLINE> <TEXT>', example: '3OSI BG VIP JUSTICE BD SUPREME COURT', description: 'Other service information' },
      { entry: '4G<SEG>*', example: '4G1*', description: 'Seat map of a segment' },
      { entry: '4G<SEG>/<SEAT>', example: '4G1/12A', description: 'Assign seats in name order (4G1/12AB), or to one passenger (4G1/12A-2.1)' },
      { entry: '4G<SEG>.XX / 4GA.XX', example: '4G1.XX', description: 'Cancel the seats of a segment, or of all' },
      { entry: '5<TEXT>', example: '5 SEND TKTS TO PSGR OFFICE', description: 'Remark' },
      { entry: '5<ITEM>.XX', example: '51.XX', description: 'Delete a remark' },
    ],
  },
  {
    title: 'PNR fields and filing',
    summary: 'Complete, end and retrieve bookings.',
    commands: [
      { entry: '7TAW/', example: '7TAW15NOV/', description: 'Ticketing time limit' },
      { entry: '6<NAME>', example: '6 RABBI', description: 'Received from' },
      { entry: 'E / ET', description: 'End transaction and file the PNR' },
      { entry: 'ER', description: 'End transaction and redisplay the PNR' },
      { entry: 'EC', description: 'End and clone names and phones into a new PNR' },
      { entry: 'IC', description: 'Ignore and clone names and phones' },
      { entry: 'IR', description: 'Ignore changes and retrieve the filed PNR again' },
      { entry: 'I / IG / IGN', description: 'Ignore — clear the work area' },
    ],
  },
  {
    title: 'Retrieve and display',
    summary: 'Find filed PNRs and show what is in the work area.',
    commands: [
      { entry: '*<LOCATOR>', example: '*OVNBNW', description: 'Retrieve a PNR by record locator' },
      { entry: '*-<LAST NAME>', example: '*-HAQUE', description: 'Search PNRs by passenger name' },
      { entry: '*<N>', example: '*2', description: 'Pick a PNR from the similar-name list' },
      { entry: '*A / *R / *I / *ALL / *P', description: 'Display the PNR in the work area' },
    ],
  },
  {
    title: 'Pricing and price quotes',
    summary: 'Fare the itinerary and store quotes.',
    commands: [
      { entry: 'WP', description: 'Price the itinerary as booked' },
      { entry: 'WPNCB', description: 'Price and rebook at the lowest available fare' },
      { entry: 'WPA', description: 'Fare list for every booking class' },
      { entry: 'WPA<AIRLINE>', example: 'WPAEK', description: 'Price with a validating carrier' },
      { entry: 'WPRQ', description: 'Price as booked and store a price quote' },
      { entry: 'PQ', description: 'Price quote summary' },
      { entry: 'PQ<N>', example: 'PQ1', description: 'Show price quote N — or, after WPA, store fare option N' },
      { entry: '*PQ / *PQ<N>', example: '*PQ2', description: 'Price quote records in full' },
    ],
  },
  {
    title: 'Printers and ticketing',
    summary: 'Ticketing authority, printers, issuing and sales.',
    commands: [
      { entry: 'PE*<PCC>', example: 'PE*KV6L', description: 'List the agency terminals (CRT) and printers (PTR)' },
      { entry: 'W*<COUNTRY>', example: 'W*BD', description: 'Ticketing authority — OK-0008, or NOTKTS when root has not allowed the user to ticket' },
      { entry: 'DSIV<LNIATA>', example: 'DSIV26EC7E', description: 'Assign one of the agency printers' },
      { entry: 'PTR/PTR<LNIATA>', example: 'PTR/PTR 26EC7E', description: 'Designate the assigned printer (space optional; PTR/26EC7E also works)' },
      { entry: 'W¥PQ<N>¥A<AIRLINE>¥F<PAYMENT>¥KP<PCT>', example: 'W¥PQ1-3¥ABG¥FINVAGT¥KP7', description: 'Issue e-tickets on a filed PNR: price quote (or range PQ1-3), validating carrier, form of payment (FINVAGT invoice agent, FCASH, FCHECK, FCC…), commission % on the base fare. Needs a printer designated, otherwise TKT PRT NOT ASSIGNED/USE W*-0004' },
      { entry: 'PE¥<EMAIL>¥', example: 'PE¥ITSSMFAISAL@GMAIL.COM¥', description: 'Add the passenger email address to the PNR' },
      { entry: '*T', description: 'Ticketing field, with a TE line for every ticket issued' },
      { entry: 'WETR*<ITEM>', example: 'WETR*2', description: 'Electronic ticket record of the ticket on that line of *T' },
      { entry: 'WETR*T<TICKET NO>', example: 'WETR*T7794884024612', description: 'Electronic ticket record by ticket number' },
      { entry: 'EMTR', description: 'Email the e-ticket itinerary and receipt to every address in the PNR — UNABLE - E TICKET NOT ISSUED until ticketed' },
      { entry: 'DQB*', example: 'DQB*28APR', description: 'Daily sales audit report — tickets issued today, or on the date given' },
    ],
  },
  {
    title: 'Encode and decode',
    summary: 'Cities, airports, airlines, aircraft and countries.',
    commands: [
      { entry: 'W/-CC<CITY>', example: 'W/-CCDHAKA', description: 'City name to city code' },
      { entry: 'W/-AP<AIRPORT>', example: 'W/-APNARITA', description: 'Airport name to airport code' },
      { entry: 'W/-AL<AIRLINE>', example: 'W/-ALBIMAN', description: 'Airline name to airline code' },
      { entry: 'W/*<CODE>', example: 'W/*DAC', description: 'Decode a city, airport (3 letters) or airline (2) — W/*NRT, W/*BG' },
      { entry: 'W/EQ-<NAME>', example: 'W/EQ-BOEING', description: 'Aircraft name to equipment codes' },
      { entry: 'W/EQ*<CODE>', example: 'W/EQ*D10', description: 'Decode an equipment code: engine and seating' },
      { entry: 'HCCC/<COUNTRY OR CODE>', example: 'HCCC/BANGLADESH', description: 'Country name to code, or code to name (HCCC/BD); small misspellings still match' },
    ],
  },
  {
    title: 'Other',
    summary: 'Session and help.',
    commands: [
      { entry: '*S', example: 'KV6L.KV6L*AZI.A', description: 'Show the PCC and agent the work area is signed in to' },
      { entry: 'AAA / *SI', description: 'Sign-in status' },
      { entry: 'HELP / ?', description: 'List supported entries in the workspace' },
    ],
  },
];

const TOTAL = GROUPS.reduce((sum, group) => sum + group.commands.length, 0);

/** Admin view of every cryptic command the workspace supports so far. */
export function FeaturesPage() {
  const [search, setSearch] = useState('');

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return GROUPS;
    return GROUPS.map((group) => ({
      ...group,
      commands: group.commands.filter((command) =>
        [command.entry, command.example ?? '', command.description, group.title].some((text) =>
          text.toLowerCase().includes(term),
        ),
      ),
    })).filter((group) => group.commands.length > 0);
  }, [search]);

  const shown = groups.reduce((sum, group) => sum + group.commands.length, 0);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Features</h1>
          <p className="page-header__subtitle">
            The host commands the workspace supports so far — {TOTAL} entries across {GROUPS.length} areas.
          </p>
        </div>
      </div>

      <div className="card-grid features__stats">
        {GROUPS.map((group) => (
          <div key={group.title} className="card">
            <div className="card__body">
              <div className="stat__label">{group.title}</div>
              <div className="stat__value">{group.commands.length}</div>
              <div className="stat__hint">{group.summary}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <SparkInput
          label="Search"
          placeholder="Entry, example or description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {groups.length === 0 && (
        <div className="table-wrap">
          <table className="data">
            <tbody>
              <tr>
                <td className="table__empty">No commands match this search.</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {groups.map((group) => (
        <section key={group.title} className="features__group">
          <h2 className="features__title">
            {group.title} <span className="features__count">{group.commands.length}</span>
          </h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Entry</th>
                  <th>Example</th>
                  <th>What it does</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {group.commands.map((command) => (
                  <tr key={command.entry}>
                    <td className="mono">{command.entry}</td>
                    <td className="mono">{command.example ?? '—'}</td>
                    <td>{command.description}</td>
                    <td>
                      <span className="badge badge--active">Available</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {search && <p className="field-hint">Showing {shown} of {TOTAL} entries.</p>}
    </>
  );
}
